import { HttpParams } from '@angular/common/http';
import { inject, NgModule } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterModule, Routes } from '@angular/router';
import { differenceWith, fromPairs, isEqual, toPairs } from 'lodash';
import { combineLatest, EMPTY } from 'rxjs';
import { pairwise, startWith, switchMap } from 'rxjs/operators';

import { buildAccountRoutes } from '@pages/account/account.routing';
import { AuditHistoryComponent } from '@pages/audit-history/audit-history.component';
import { buildBudgetRoutes } from '@pages/budget-page/budget-page.routing';
import { buildPeriodCloseRoutes } from '@pages/closing-page/closing-page.routing';
import { DashboardComponent } from '@pages/dashboard/dashboard.component';
import { DesignSystemComponent } from '@pages/design-system/design-system.component';
import { DocumentsComponent } from '@pages/documents/documents.component';
import { buildForecastRoutes } from '@pages/forecast-accruals-page/forecast.routing';
import { buildInvestigatorRoutes } from '@pages/investigator/investigator.routing';
import { OpsAdminComponent } from '@pages/ops-admin/ops-admin.component';
import { PortfolioDashboardComponent } from '@pages/portfolio-dashboard/portfolio-dashboard.component';
import { QaTestingComponent } from '@pages/qa-testing/qa-testing.component';
import { RiskAnalyticsComponent } from '@pages/risk-analytics/risk-analytics.component';
import { ScenarioManagerComponent } from '@pages/scenario/scenario-manager/scenario-manager.component';
import { ScenarioComponent } from '@pages/scenario/scenario.component';
import { buildSettingsRoutes } from '@pages/settings/settings.routing';
import { buildTrialInsightsRoutes } from '@pages/trial-insights/trial-insights.routing';

import { MainLayoutComponent } from '@layouts/main-layout/main-layout.component';

import { CommonConstants } from '@shared/constants/common.constants';
import { PagesToRetriggerCanActivate } from '@shared/constants/pages-to-retrigger-can-activate.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { AuthGuard } from '@shared/guards/auth.guard';
import { CanDeactivateGuard } from '@shared/guards/can-deactivate.guard';
import { EventType } from '@shared/services/gql.service';
import { Flags, LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { reflectOnFeatureFlagChange } from '@shared/services/routing.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';

// eslint-disable-next-line max-lines-per-function
const buildRoutes = (featureFlags: Flags | null, lastUrl: string): Routes => [
  {
    loadComponent: () => import('./pages/login/login.component').then((c) => c.LoginComponent),
    path: ROUTING_PATH.LOGIN,
  },
  {
    loadComponent: () =>
      import('./pages/forgot-password/forgot-password.component').then(
        (c) => c.ForgotPasswordComponent
      ),
    path: ROUTING_PATH.FORGOT_PASSWORD,
  },
  {
    loadComponent: () =>
      import('./pages/confirmation/confirmation.component').then((c) => c.ConfirmationComponent),
    path: ROUTING_PATH.CONFIRMATION,
  },
  {
    canActivateChild: [AuthGuard],
    loadChildren: () =>
      import('./pages/onboarding/onboarding-routing.routes').then((m) => m.routes),
    path: ROUTING_PATH.ONBOARDING.INDEX,
  },
  {
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./pages/system-maintenance/system-maintenance.component').then(
        (c) => c.SystemMaintenanceComponent
      ),
    path: ROUTING_PATH.SYSTEM_MAINTENANCE,
  },
  {
    canActivateChild: [AuthGuard],
    children: [
      reflectOnFeatureFlagChange(
        'nav_trial_insights',
        featureFlags,
        ROUTING_PATH.TRIAL_INSIGHTS.INDEX,
        buildTrialInsightsRoutes
      ),
      reflectOnFeatureFlagChange(
        'nav_budget',
        featureFlags,
        ROUTING_PATH.BUDGET.INDEX,
        buildBudgetRoutes
      ),
      reflectOnFeatureFlagChange(
        'nav_forecast',
        featureFlags,
        ROUTING_PATH.FORECAST_ROUTING.INDEX,
        buildForecastRoutes
      ),
      reflectOnFeatureFlagChange(
        'tab_forecast_in_month',
        featureFlags,
        ROUTING_PATH.CLOSING.INDEX,
        buildPeriodCloseRoutes,
        lastUrl
      ),
      reflectOnFeatureFlagChange(
        'nav_investigator',
        featureFlags,
        ROUTING_PATH.INVESTIGATOR.INDEX,
        buildInvestigatorRoutes
      ),
      buildSettingsRoutes(),
      buildAccountRoutes(featureFlags),
      {
        component: PortfolioDashboardComponent,
        path: ROUTING_PATH.HOME,
      },
      {
        children: [
          { component: ScenarioManagerComponent, path: '' },
          { path: '**', redirectTo: '' },
        ],
        component: ScenarioComponent,
        path: ROUTING_PATH.MANAGER,
      },
      reflectOnFeatureFlagChange(
        'nav_audit_history',
        featureFlags,
        ROUTING_PATH.AUDIT_HISTORY,
        () => ({
          component: AuditHistoryComponent,
          path: ROUTING_PATH.AUDIT_HISTORY,
        })
      ),
      reflectOnFeatureFlagChange(
        'nav_document_library',
        featureFlags,
        ROUTING_PATH.DOCUMENTS,
        () => ({
          canDeactivate: [CanDeactivateGuard],
          component: DocumentsComponent,
          path: ROUTING_PATH.DOCUMENTS,
        })
      ),
      {
        component: RiskAnalyticsComponent,
        path: ROUTING_PATH.RISK_ANALYTICS,
      },
      {
        component: DashboardComponent,
        path: ROUTING_PATH.DASHBOARD,
      },
      reflectOnFeatureFlagChange(
        'nav_design_system',
        featureFlags,
        ROUTING_PATH.DESIGN_SYSTEM,
        () => ({
          component: DesignSystemComponent,
          loadChildren: () =>
            import('./pages/design-system/design-system-routing.routes').then((m) => m.routes),
          path: ROUTING_PATH.DESIGN_SYSTEM,
        })
      ),
      reflectOnFeatureFlagChange(
        'nav_ops_admin',
        featureFlags,
        ROUTING_PATH.OPS_ADMIN.INDEX,
        () => ({
          component: OpsAdminComponent,
          loadChildren: () =>
            import('./pages/ops-admin/ops-admin-routing.routes').then((m) => m.routes),
          path: ROUTING_PATH.OPS_ADMIN.INDEX,
        })
      ),
      reflectOnFeatureFlagChange(
        'nav_qa_testing',
        featureFlags,
        ROUTING_PATH.QA_TESTING.INDEX,
        () => ({
          component: QaTestingComponent,
          loadChildren: () => import('./pages/qa-testing/qa-testing.routes').then((m) => m.routes),
          path: ROUTING_PATH.QA_TESTING.INDEX,
        })
      ),
      {
        path: '**',
        redirectTo: featureFlags?.nav_portfolio ? 'home' : 'budget',
      },
    ],
    component: MainLayoutComponent,
    path: '',
  },
  {
    path: '**',
    redirectTo: '',
  },
];

@NgModule({
  exports: [RouterModule],
  imports: [RouterModule.forRoot([])],
})
export class AppRoutingModule {
  private activatedRoute = inject(ActivatedRoute);
  private eventService = inject(EventService);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private router = inject(Router);

  private appRouteList: string[] = [];
  private lastUrl = '';

  constructor() {
    this.launchDarklyService.loaded$
      .pipe(
        switchMap((loaded) => {
          if (!loaded) {
            return EMPTY;
          }

          return this.launchDarklyService.flags$;
        }),
        startWith(this.launchDarklyService.$flags()),
        pairwise()
      )
      .subscribe(([prevFlags, flags]) => {
        this.updateRoutesAndInitNavigation(buildRoutes(flags, this.lastUrl), prevFlags);
        this.systemMaintenanceRedirect();
      });

    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.lastUrl = event.urlAfterRedirects;
        const trialId = this.mainQuery.getValue().trialKey;

        if (trialId) {
          this.setTrialIdQueryParameter(trialId);
        }
      }
    });

    combineLatest([
      this.eventService.select$(EventType.TRIAL_CHANGED),
      this.mainQuery.select('trialKey'),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([{ trial_id }, trialKey]) => {
        const trialId = trialKey ? trialKey : trial_id;

        this.setTrialIdQueryParameter(trialId);
      });
  }

  private getAppRouteList(parent: string, config: Routes): void {
    for (let i = 0; i < config.length; i++) {
      const route = config[i];

      if (!route.redirectTo) {
        this.appRouteList = [...this.appRouteList, `${parent}/${route.path}`];
      }

      if (route.children) {
        const currentPath = route.path ? `${parent}/${route.path}` : parent;

        this.getAppRouteList(currentPath, route.children);
      }
    }
  }

  private refreshCanActivateGuards(prevFlags?: Flags): void {
    if (!prevFlags) {
      return;
    }

    const flags = this.launchDarklyService.$flags();
    const flagsChanges = Object.keys(
      fromPairs(differenceWith(toPairs(prevFlags), toPairs(flags), isEqual))
    );

    if (
      PagesToRetriggerCanActivate.some(
        (path) => this.router.url.includes(path.value) && flagsChanges.includes(path.flag)
      )
    ) {
      void this.router.navigateByUrl(this.router.url);
    }
  }

  private setTrialIdQueryParameter(trialId: string) {
    void this.router.navigate([], {
      queryParams: {
        [CommonConstants.TRIAL_ID_QUERY_PARAMETER_NAME]: trialId,
        utm_campaign: null,
        utm_medium: null,
        utm_source: null,
      },
      queryParamsHandling: 'merge',
      relativeTo: this.activatedRoute,
      replaceUrl: true,
    });
  }

  private systemMaintenanceRedirect(): void {
    const nav =
      this.launchDarklyService.flags$.getValue().nav_system_maintenance_with_messages.messages;
    const route = `/${ROUTING_PATH.SYSTEM_MAINTENANCE}`;
    // sometimes `router.url` doesn't give us the correct url,
    // so instead I'm looking at the window location here
    const isRouteMaintenance = window.location.pathname.includes(route);

    if (nav && !isRouteMaintenance) {
      void this.router.navigate([route]);
    } else if (!nav && isRouteMaintenance) {
      void this.router.navigate([`/`]);
    }
  }

  private updateAppRouteList(): void {
    this.appRouteList = [];
    this.getAppRouteList('', this.router.config);
  }

  private updateRoutesAndInitNavigation(routes: Routes, prevFlags?: Flags): void {
    this.router.resetConfig([...routes]);
    this.updateAppRouteList();

    const urlParts = this.router.url.split('?');

    if (urlParts[0] !== '/') {
      if (!this.appRouteList.includes(urlParts[0])) {
        if (urlParts[1]) {
          const httpParams = new HttpParams({ fromString: urlParts[1] });
          const queryParams = httpParams.keys().reduce((result, k) => {
            return {
              ...result,
              [k]: httpParams.get(k),
            };
          }, {});

          void this.router.navigate([urlParts[0]], { queryParams });
        } else {
          void this.router.navigate([this.router.url]);
        }
      } else {
        this.refreshCanActivateGuards(prevFlags);
      }
    }
  }
}
